import { patientBffApi } from '@/modules/baseApi';
import { InformedConsent } from '@/modules/informedConsent/informedConsent.model';
import { InformedConsentService } from '@/modules/informedConsent/informedConsent.service';
import { MedicalDocumentType, useMedicalDocuments } from '@/modules/document';
import { useMemo } from 'react';
import { MediaService } from '@ianesthesie-kit/services';
import { useIsVerified } from '@/modules/userConfig';

export const informedConsentApi = patientBffApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    informedConsent: builder.query<InformedConsent, void>({
      queryFn: () =>
        InformedConsentService.fetchInformedConsent()
          .then((data) => ({
            data: data || ({} as any)
          }))
          .catch((error) => ({ error })),
      providesTags: ['InformedConsent']
    }),

    signInformedConsent: builder.mutation<unknown, InformedConsent['items']>({
      queryFn: (payload) =>
        InformedConsentService.signInformedConsent(payload)
          .then(() => ({
            data: {}
          }))
          .catch((error) => ({ error })),
      invalidatesTags: ['InformedConsent']
    })
  })
});

export const { useInformedConsentQuery, useSignInformedConsentMutation } =
  informedConsentApi;

type QueryOpts = Parameters<typeof useInformedConsentQuery>[1];

export const useHasPendingConsent = (opts?: QueryOpts) => {
  const isVerified = useIsVerified();
  const { currentData, isLoading } = useInformedConsentQuery(undefined, {
    ...opts,
    skip: !isVerified || !!opts?.skip
  });

  return {
    hasPendingConsent: InformedConsentService.isPending(currentData),
    isLoadingConsent: isLoading
  };
};

export const useDownloadConsentFormMedium = () => {
  const { currentData: consent } = useInformedConsentQuery();
  const { currentData: docs } = useMedicalDocuments(undefined, {
    skip: !consent?.consentSigned
  });

  return useMemo(() => {
    const medium = docs?.[MedicalDocumentType.CONSENT_FORM]?.[0]?.media[0];
    const downloadConsentForm = () => {
      if (medium) {
        return MediaService.getInstance().download(medium, {
          presignedUrl: true
        });
      }
      return Promise.resolve(undefined);
    };

    return {
      consentFormExists: !!medium,
      downloadConsentForm
    };
  }, [docs]);
};
